import type { Module } from 'vuex';
import { LOCAL_STORAGE_KEYS, setLocalStorageValue, removeLocalStorageValue, getLocalStorageValue } from '@/constants/localStorage';
import {RootState} from "@/store";

export interface ConnectionState {
  wsConnected: boolean;
  wsLoaded: boolean;
  wsSession: string;
  lastTimeBuild: number | null;
  isClearedBrowser: boolean;
}

const connectionModule: Module<ConnectionState, RootState> = {
  namespaced: true,

  state: (): ConnectionState => ({
    wsConnected: false,
    wsLoaded: false,
    wsSession: '',
    lastTimeBuild: null,
    isClearedBrowser: false,
  }),

  getters: {
    isConnected: (state): boolean => state.wsConnected,
    isLoaded: (state): boolean => state.wsLoaded,
    getWsSession: (state): string => state.wsSession,
    buildTime: (state): number | null => state.lastTimeBuild,
    isClearedBrowser: (state): boolean => state.isClearedBrowser,
  },

  mutations: {
    setWsConnected(
      state,
      payload: boolean
    ) {
      state.wsConnected = payload;
    },

    setLoaded(
      state,
      payload: boolean
    ) {
      state.wsLoaded = payload;
    },

    setWsSession(
      state,
      payload: string
    ) {
      state.wsSession = payload;
    },

    setLastTimeBuild(
      state,
      payload: number | null
    ) {
      state.lastTimeBuild = payload;
      if (state.lastTimeBuild === null) {
        removeLocalStorageValue(LOCAL_STORAGE_KEYS.LAST_TIME_BUILD);
      } else if (getLocalStorageValue(LOCAL_STORAGE_KEYS.LAST_TIME_BUILD) !== state.lastTimeBuild.toString()) {
        setLocalStorageValue(LOCAL_STORAGE_KEYS.LAST_TIME_BUILD, state.lastTimeBuild.toString());
      }
    },

    initSession(state, forceUpdate = false) {
      function makeSessionId(length: number): string {
        let result = '';
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        for (let i = 0; i < length; i++) {
          result += characters.charAt(Math.floor(Math.random() * characters.length));
        }
        return result;
      }

      const storedSession = getLocalStorageValue(LOCAL_STORAGE_KEYS.WS_SESSION);
      if (forceUpdate || storedSession === null || storedSession === '') {
        const newSession = makeSessionId(32);
        state.wsSession = newSession;
        setLocalStorageValue(LOCAL_STORAGE_KEYS.WS_SESSION, newSession);
      } else {
        state.wsSession = storedSession;
      }
    },

    setIsClearedBrowser(
      state,
      payload: boolean
    ) {
      state.isClearedBrowser = payload;
    },
  },

  actions: {
    setWsConnected({ commit }, payload) {
      commit('setWsConnected', payload);
    },

    setLoaded({ commit }, payload) {
      commit('setLoaded', payload);
    },

    setLastTimeBuild({ commit }, payload) {
      commit('setLastTimeBuild', payload);
    },

    initSession({ commit }, forceUpdate = false) {
      commit('initSession', forceUpdate);
    },

    wsSetLoaded({ commit }, payload) {
      commit('setLoaded', payload);
    },

    setIsClearedBrowser({ commit }, payload) {
      commit('setIsClearedBrowser', payload);
    },
  },
};

export default connectionModule; 